import React, { useState } from 'react';
import AccountSessionError from './AccountSessionError';
import {
  clearStoredAuthIfUnauthorized,
  performWebLogout,
  POST_LOGIN_REDIRECT_STORAGE_KEY,
  readStoredAuth,
  SIGN_IN_REQUIRED_MESSAGE,
} from './profileFormUtils';
import {
  apiDeleteAccount,
  deleteAccountErrorMessage as mapDeleteAccountApiError,
} from './authRecoveryApi';
import './AccountLayout.css';

const AccountDelete = () => {
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleDelete = async () => {
    setError('');
    setSuccess('');
    if (!confirmed) {
      setError('Please confirm that you understand this action is permanent.');
      return;
    }

    const { token } = readStoredAuth();
    if (!token) {
      performWebLogout();
      setError(SIGN_IN_REQUIRED_MESSAGE);
      return;
    }

    setDeleting(true);
    try {
      const { res, data } = await apiDeleteAccount(token);
      if (!res.ok) {
        clearStoredAuthIfUnauthorized(res.status);
        throw new Error(mapDeleteAccountApiError(res.status, data));
      }
      let successMsg = 'Account deleted successfully.';
      if (data && typeof data.message === 'string') {
        const trimmed = data.message.trim();
        if (trimmed) successMsg = trimmed;
      }
      setSuccess(successMsg);
      try {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      performWebLogout();
      try {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      // Hard navigate so AccountLayout cannot intercept with a login redirect
      // after auth is cleared on this protected route.
      window.location.replace('/');
    } catch (e) {
      setError(e.message || 'Could not delete account.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="account-page">
      <h1>Delete account</h1>
      <p className="account-page__muted">
        This permanently removes your DigiRasa account, profile, and associated data. Orders and
        subscriptions cannot be recovered.
      </p>

      <div
        className="account-page__note account-page__note--danger"
        style={{ marginBottom: '20px' }}
      >
        <strong>This cannot be undone.</strong> Your access tokens will stop working immediately
        after deletion.
      </div>

      {error ? (
        <AccountSessionError
          message={error}
          returnPath="/account/delete"
          signInSuffix="to continue"
          variant="inline"
          className="account-delete__session-error"
        />
      ) : null}
      {success ? (
        <div
          className="auth-inline-message auth-inline-message--success"
          style={{ marginBottom: '12px' }}
        >
          {success}
        </div>
      ) : null}

      <label
        className="account-delete__confirm"
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          marginBottom: '20px',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          style={{ marginTop: '4px' }}
        />
        <span>
          I understand my account and data will be <strong>permanently deleted</strong> and I will
          be signed out.
        </span>
      </label>

      <button
        type="button"
        className="auth-form__submit"
        style={{
          background: confirmed ? '#b42318' : undefined,
          borderColor: confirmed ? '#b42318' : undefined,
        }}
        disabled={deleting || !!success || !confirmed}
        onClick={handleDelete}
      >
        {deleting ? 'Deleting…' : 'Delete my account permanently'}
      </button>
    </div>
  );
};

export default AccountDelete;
